/**
 * React Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing.
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Container, Title, Text, Button, Stack, Alert } from '@mantine/core';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';
import { errorLogger } from '@shared/services/ErrorLogger';
import { ReactErrorContext } from '@shared/types/ErrorLogging';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  boundaryName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error with React-specific context
    this.logReactError(error, errorInfo);

    // Update state with error info
    this.setState({ errorInfo });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  private logReactError(error: Error, errorInfo: ErrorInfo) {
    const reactContext: ReactErrorContext = {
      type: 'react_error_boundary',
      componentStack: errorInfo.componentStack,
      errorBoundary: this.props.boundaryName || 'ErrorBoundary',
      componentName: this.extractComponentName(errorInfo.componentStack),
    };

    errorLogger.logReactError(error, reactContext);
  }

  private extractComponentName(componentStack: string): string {
    // Extract the component name from the component stack
    const match = componentStack.match(/^\s*in (\w+)/);
    return match ? match[1] : 'Unknown';
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <Container size="sm" py="xl">
          <Stack align="center" gap="md">
            <Alert
              variant="light"
              color="red"
              title="Something went wrong"
              icon={<IconAlertTriangle size={16} />}
              w="100%"
            >
              <Text size="sm">
                An unexpected error occurred in this part of the application.
                This error has been automatically reported to help us improve
                the experience.
              </Text>
            </Alert>

            <Stack align="center" gap="sm">
              <Title order={3} ta="center" c="dimmed">
                Application Error
              </Title>

              {import.meta.env.DEV && this.state.error && (
                <details style={{ marginTop: '1rem', width: '100%' }}>
                  <summary
                    style={{ cursor: 'pointer', marginBottom: '0.5rem' }}
                  >
                    <Text size="sm" fw={500}>
                      Error Details (Development)
                    </Text>
                  </summary>
                  <Text
                    size="xs"
                    ff="monospace"
                    c="red"
                    style={{ whiteSpace: 'pre-wrap' }}
                  >
                    {this.state.error.message}
                    {'\n\n'}
                    {this.state.error.stack}
                  </Text>
                  {this.state.errorInfo && (
                    <Text
                      size="xs"
                      ff="monospace"
                      c="orange"
                      style={{ whiteSpace: 'pre-wrap' }}
                    >
                      {'\n\nComponent Stack:'}
                      {this.state.errorInfo.componentStack}
                    </Text>
                  )}
                </details>
              )}

              <Stack gap="sm" mt="md" style={{ flexDirection: 'row' }}>
                <Button
                  variant="outline"
                  leftSection={<IconRefresh size={16} />}
                  onClick={this.handleRetry}
                >
                  Try Again
                </Button>
                <Button
                  leftSection={<IconRefresh size={16} />}
                  onClick={this.handleReload}
                >
                  Reload Page
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Container>
      );
    }

    return this.props.children;
  }
}

/**
 * Higher-Order Component for easier usage
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
}
